import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { TrendingUp, DollarSign, Package, Crosshair, Eye, Users, Target, Percent } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import { fetchAnalyticsOverview, api } from "@/lib/api";
import { useBranding } from "@/lib/branding";

const RANGES = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "mtd", label: "MTD" },
  { id: "ytd", label: "YTD" },
  { id: "all", label: "All time" },
];

const fmtEUR = (n) =>
  new Intl.NumberFormat("en-EU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);
const fmtDay = (d) => {
  try {
    const dt = new Date(d + "T00:00:00Z");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
};

function KpiCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const tones = {
    default: { bg: "#FFFFFF", accent: "#03142f" },
    success: { bg: "#F0FDF4", accent: "#047857" },
    warning: { bg: "#FFFBEB", accent: "#B45309" },
    danger: { bg: "#FFF1F2", accent: "#BE123C" },
  };
  const t = tones[tone] || tones.default;
  return (
    <div
      data-testid={testid}
      className="border border-[#eaecf0] p-5 flex flex-col justify-between min-h-[112px]"
      style={{ background: t.bg }}
    >
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        {Icon ? <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: t.accent }} /> : null}
      </div>
      <div>
        <div className="font-display font-semibold text-3xl tracking-tight tabular-nums" style={{ color: t.accent }}>
          {value}
        </div>
        {sub ? <div className="text-[11px] font-mono text-[#525252] mt-1">{sub}</div> : null}
      </div>
    </div>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="overline">{children}</div>
      {right}
    </div>
  );
}

export default function Analytics() {
  const brand = useBranding();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [bundleData, setBundleData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (r) => {
    setLoading(true);
    try {
      const d = await fetchAnalyticsOverview(r);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analytics load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
    api.get("/bundle-analytics").then((r) => setBundleData(r.data)).catch(() => {});
    const iv = setInterval(() => load(range), 15000);
    return () => clearInterval(iv);
  }, [range]);

  const k = useMemo(() => data?.kpis || {}, [data]);
  const series = useMemo(() => (data?.time_series || []).map((d) => ({ ...d, day: fmtDay(d.date) })), [data]);
  const registrarBars = useMemo(
    () => (data?.registrar_performance || []).map((r) => ({ ...r, name: r.registrar.toUpperCase() })),
    [data],
  );
  const currencyLine = useMemo(() => {
    const m = k.revenue_by_currency || {};
    return Object.entries(m)
      .map(([cur, val]) => `${cur} ${fmtNum(val)}`)
      .join(" · ") || "—";
  }, [k]);

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="analytics-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="overline">{brand.brand_name}</span>
            <span className="text-[#D4D4D8]">/</span>
            <span className="text-sm font-medium">Analytics</span>
          </div>
          <div className="flex items-center gap-1" data-testid="range-picker">
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                data-testid={`range-${r.id}`}
                className={`text-[11px] font-mono uppercase px-3 py-1.5 border ${
                  range === r.id
                    ? "border-[#03142f] bg-[#03142f] text-white"
                    : "border-[#eaecf0] text-[#525252] hover:bg-[#FAFAFA]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6 space-y-8">
          {/* KPI Row 1 */}
          <section>
            <SectionTitle>Revenue &amp; Sales</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                testid="kpi-revenue"
                label="Gross revenue"
                icon={DollarSign}
                tone="success"
                value={fmtEUR(k.revenue_total)}
                sub={currencyLine}
              />
              <KpiCard
                testid="kpi-sold"
                label="Domains sold"
                icon={TrendingUp}
                tone="success"
                value={fmtNum(k.sold_count)}
                sub={`avg ${fmtEUR(k.avg_sale_price)} per sale`}
              />
              <KpiCard
                testid="kpi-conversion"
                label="Conversion rate"
                icon={Percent}
                value={`${(k.conversion_rate ?? 0).toFixed(2)}%`}
                sub={`${fmtNum(k.sold_count)} sales / ${fmtNum(k.total_views)} views`}
              />
              <KpiCard
                testid="kpi-offers"
                label="Offers received"
                icon={Target}
                value={fmtNum(k.offer_count)}
                sub={`avg ${fmtEUR(k.avg_offer_amount)} per offer`}
              />
            </div>
          </section>

          {/* Bundle vs bare comparison */}
          {bundleData && (bundleData.bundle.listings > 0 || bundleData.bare.listings > 0) && (
            <section data-testid="bundle-analytics-section">
              <SectionTitle>Brand Starter bundle · vs bare domains</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BundleCohortCard label="Brand Starter bundles" tint="#dbeafe" fg="#1e40af" data={bundleData.bundle} />
                <BundleCohortCard label="Bare domains" tint="#f3f4f6" fg="#4b5563" data={bundleData.bare} />
              </div>
              <div className="text-[11px] text-[#98a2b3] mt-2 px-1">
                Compares total views · offers · sold listings for the two cohorts to see if bundling drives price acceptance.
              </div>
            </section>
          )}

          {/* KPI Row 2 */}
          <section>
            <SectionTitle>Inventory &amp; Sniping</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                testid="kpi-active"
                label="Active listings"
                icon={Package}
                value={fmtNum(k.active_listings)}
                sub={`portfolio value ${fmtEUR(k.portfolio_value)}`}
              />
              <KpiCard
                testid="kpi-avg-listing"
                label="Avg listing price"
                icon={DollarSign}
                value={fmtEUR(k.inventory_avg_price)}
                sub={`${fmtNum(k.sold_listings_all_time)} sold all time`}
              />
              <KpiCard
                testid="kpi-snipes-success-rate"
                label="Snipe success rate"
                icon={Crosshair}
                tone={k.success_rate >= 50 ? "success" : k.success_rate === 0 ? "default" : "warning"}
                value={`${(k.success_rate ?? 0).toFixed(1)}%`}
                sub={`${fmtNum(k.snipes_success)} wins / ${fmtNum(k.snipes_failed)} losses`}
              />
              <KpiCard
                testid="kpi-traffic"
                label="Traffic"
                icon={Eye}
                value={fmtNum(k.total_views)}
                sub={`${fmtNum(k.unique_visitors)} unique visitors`}
              />
            </div>
          </section>

          {/* Chart: views + revenue over time */}
          <section>
            <SectionTitle>
              Activity over time
              {loading ? <span className="ml-3 text-[10px] font-mono text-[#A1A1AA]">loading…</span> : null}
            </SectionTitle>
            <div className="border border-[#eaecf0] p-4 bg-white" data-testid="activity-chart">
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <AreaChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fillViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#03142f" stopOpacity={0.28} />
                        <stop offset="95%" stopColor="#03142f" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#047857" stopOpacity={0.32} />
                        <stop offset="95%" stopColor="#047857" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#F4F4F5" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#525252" }} width={40} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#525252" }} width={50} />
                    <Tooltip
                      contentStyle={{
                        border: "1px solid #eaecf0",
                        borderRadius: 2,
                        fontFamily: "IBM Plex Sans",
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="views"
                      stroke="#03142f"
                      strokeWidth={2}
                      fill="url(#fillViews)"
                      name="Views"
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#047857"
                      strokeWidth={2}
                      fill="url(#fillRev)"
                      name="Revenue (€)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Two-column: registrar + top domains */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <SectionTitle>Registrar performance</SectionTitle>
              <div className="border border-[#eaecf0] p-4 bg-white" data-testid="registrar-chart">
                {registrarBars.length === 0 ? (
                  <div className="text-sm text-[#525252] py-16 text-center">No snipe attempts in this range.</div>
                ) : (
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={registrarBars} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#F4F4F5" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#525252" }} width={30} />
                        <Tooltip
                          contentStyle={{
                            border: "1px solid #eaecf0",
                            borderRadius: 2,
                            fontFamily: "IBM Plex Sans",
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                        <Bar dataKey="wins" fill="#047857" name="Wins" />
                        <Bar dataKey="losses" fill="#BE123C" name="Losses" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div>
              <SectionTitle>Top domains by revenue</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="top-revenue-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Domain</th>
                      <th className="text-right px-4 py-2.5 overline">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.top_domains_by_revenue || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No sales in this range.
                        </td>
                      </tr>
                    )}
                    {(data?.top_domains_by_revenue || []).map((r) => (
                      <tr key={r.domain} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 font-mono">{r.domain}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#047857]">
                          {fmtEUR(r.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Three-column: top views, referrers, utm */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <SectionTitle>Top domains by views</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="top-views-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Domain</th>
                      <th className="text-right px-4 py-2.5 overline">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.top_domains_by_views || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No traffic yet.
                        </td>
                      </tr>
                    )}
                    {(data?.top_domains_by_views || []).map((r) => (
                      <tr key={r.domain} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 font-mono">{r.domain}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <SectionTitle>Top referrers</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="referrers-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Source</th>
                      <th className="text-right px-4 py-2.5 overline">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.top_referrers || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No referrers tracked.
                        </td>
                      </tr>
                    )}
                    {(data?.top_referrers || []).map((r) => (
                      <tr key={r.source} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 text-xs font-mono truncate max-w-[240px]">{r.source}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <SectionTitle>UTM sources</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="utm-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Source</th>
                      <th className="text-right px-4 py-2.5 overline">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.utm_sources || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No UTM tracked.
                        </td>
                      </tr>
                    )}
                    {(data?.utm_sources || []).map((r) => (
                      <tr key={r.source} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 text-xs font-mono">{r.source}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Shares */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SectionTitle>Most shared domains</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="top-shares-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Domain</th>
                      <th className="text-right px-4 py-2.5 overline">Shares</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.shares?.top_domains || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No shares yet — the share row lives on every lander.
                        </td>
                      </tr>
                    )}
                    {(data?.shares?.top_domains || []).map((r) => (
                      <tr key={r.domain} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 font-mono">{r.domain}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.shares)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <SectionTitle>Share channels</SectionTitle>
              <div className="border border-[#eaecf0] bg-white" data-testid="share-channels-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                      <th className="text-left px-4 py-2.5 overline">Channel</th>
                      <th className="text-right px-4 py-2.5 overline">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.shares?.by_channel || []).length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-[#525252]">
                          No share clicks tracked.
                        </td>
                      </tr>
                    )}
                    {(data?.shares?.by_channel || []).map((r) => (
                      <tr key={r.channel} className="border-b border-[#eaecf0]">
                        <td className="px-4 py-2.5 text-xs font-mono capitalize">{r.channel}</td>
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div
                className="border border-t-0 border-[#eaecf0] bg-white px-4 py-3 text-xs font-mono text-[#525252] flex items-center justify-between"
                data-testid="share-discount-stats"
              >
                <span>Discount codes</span>
                <span className="tabular-nums text-[#101828]">
                  {fmtNum(data?.shares?.codes_issued || 0)} issued · {fmtNum(data?.shares?.codes_redeemed || 0)} redeemed
                </span>
              </div>
            </div>
          </section>

          <div className="text-[10px] font-mono text-[#A1A1AA] pt-4 flex items-center gap-2">
            <Users className="w-3 h-3" strokeWidth={1.5} />
            Data window: {data?.since ? new Date(data.since).toLocaleString() : "all time"} → now · auto-refresh 15s
          </div>
        </div>
      </main>
    </div>
  );
}


function BundleCohortCard({ label, tint, fg, data }) {
  const conv = data.close_rate;
  return (
    <div
      className="grid-panel p-5"
      style={{ borderLeft: `3px solid ${fg}` }}
      data-testid={`bundle-cohort-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
          style={{ background: tint, color: fg }}
        >
          {label}
        </span>
        <span className="text-[11px] text-[#98a2b3]">{data.listings} listings</span>
      </div>
      <div className="grid grid-cols-4 gap-3 text-center">
        <div>
          <div className="text-xl font-bold text-[#101828]">{data.views.toLocaleString()}</div>
          <div className="text-[9px] uppercase tracking-wider text-[#98a2b3] mt-1">Views</div>
        </div>
        <div>
          <div className="text-xl font-bold text-[#101828]">{data.offers}</div>
          <div className="text-[9px] uppercase tracking-wider text-[#98a2b3] mt-1">Offers</div>
        </div>
        <div>
          <div className="text-xl font-bold text-[#101828]">{data.sold}</div>
          <div className="text-[9px] uppercase tracking-wider text-[#98a2b3] mt-1">Sold</div>
        </div>
        <div>
          <div className="text-xl font-bold" style={{ color: fg }}>{conv}%</div>
          <div className="text-[9px] uppercase tracking-wider text-[#98a2b3] mt-1">Close rate</div>
        </div>
      </div>
    </div>
  );
}

